import { TAG_ERRORS } from '@locales/id/errors';
import { ApiError, errorText } from './client';
import type { CylinderStatus } from './common';
import { ERROR_CODE } from './errors';
import { query, type OffsetPage } from './paging';

/** The cylinder a tag was bound to before (its chip under another EPC, or its own retired binding). */
export interface TagUse {
  cylinderId: number;
  serialNo: string;
}

/**
 * A tag's place in the tag registry (backend 0.15): a tag is bound to one cylinder, once. `usedBy` names the cylinder
 * it was bound to when no cylinder carries its EPC now (a rewritten chip, a retired tag): never "unregistered".
 */
export interface TagRegistryState {
  usedBy: TagUse | null;
  /** its binding ended (Ganti tag, a deleted cylinder): it is never bound again */
  retired: boolean;
}

/** A tag in front of a reader right now, with the cylinder it belongs to (when registered). */
export interface PresentTag extends TagRegistryState {
  epc: string;
  tid: string | null;
  gwId: string;
  gwLabel: string | null;
  firstSeen: string;
  lastSeen: string;
  locationId: number | null;
  locationName: string | null;
  cylinderId: number | null;
  serialNo: string | null;
  cylinderStatus: CylinderStatus | null;
  registeredTid: string | null;
  /** the chip id differs from the registered one: a possible clone */
  tidMismatch: boolean;
  /** no full chip TID read yet: registration waits for it */
  tidMissing: boolean;
  /** never bound (by EPC or chip) and its TID read: the only tag Simpan or Ganti tag may take */
  registrable: boolean;
}
export type PresentTagPage = OffsetPage<PresentTag>;

/** Exactly one of the two: the backend refuses neither (400 FILTER_REQUIRED). */
export type PresentScope = { gwId: string; locationId?: never } | { locationId: number; gwId?: never };

export const tagPaths = {
  /** newest read first */
  present: (scope: PresentScope, limit: number) => `/tags/present${query({ ...scope, limit })}`,
};

/** Refusals whose server sentence says more than ours: the cylinder that used the tag, the place it must be read at. */
const SERVER_WORDED = new Set<string>([ERROR_CODE.TAG_ALREADY_USED, ERROR_CODE.TAG_READ_ELSEWHERE]);

/** The failure of a registration or a Ganti tag as people read it: a tag refusal in its own words (`extra` first). */
export function tagErrorText(e: unknown, extra: Record<string, string> = {}): string {
  if (!(e instanceof ApiError)) return errorText(e);
  if (SERVER_WORDED.has(e.code) && e.message) return e.message;
  return extra[e.code] ?? TAG_ERRORS[e.code] ?? e.message;
}

/** A tag refusal that a fresh read may change (the tag list is reloaded after it). */
export const isTagRefusal = (e: unknown) => e instanceof ApiError && e.code in TAG_ERRORS;
